import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

export interface SelectOption {
  value: string;
  label: string;
}

/** A dropdown wired to react-hook-form. `emptyLabel` adds a first "not set" choice. */
export function FormSelect<T extends FieldValues>({
  name,
  control,
  options,
  emptyLabel,
  helperText,
  ...rest
}: Omit<TextFieldProps, 'name' | 'select' | 'value' | 'onChange' | 'error'> & { name: FieldPath<T>; control: Control<T>; options: readonly SelectOption[]; emptyLabel?: string }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...rest}
          select
          fullWidth
          name={field.name}
          disabled={rest.disabled ?? field.disabled}
          value={field.value ?? ''}
          onChange={field.onChange}
          onBlur={field.onBlur}
          inputRef={field.ref}
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? helperText}
          slotProps={{ ...rest.slotProps, select: { displayEmpty: !!emptyLabel }, inputLabel: { shrink: emptyLabel ? true : undefined } }}
        >
          {emptyLabel !== undefined && (
            <MenuItem value="">
              <span style={{ color: 'inherit', opacity: 0.7 }}>{emptyLabel}</span>
            </MenuItem>
          )}
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}

export const toOptions = (values: readonly string[], label: (v: string) => string = (v) => v): SelectOption[] => values.map((v) => ({ value: v, label: label(v) }));
